import { Breadcrumb } from "@app/shared/components/Breadcrumb";
import { formatLocalizedDate, formatRelativeTime } from "@core/shared/utils";
import { Tabs } from "@design-system/components/Tabs";
import { Tab } from "@design-system/components/Tabs/Tab";
import { trans } from "@mongez/localization";
import { useEffect, useState } from "react";
import { offerAtom } from "../../../../core/offers/atoms";
import {
  OfferDescription,
  OfferHeader,
  OfferLessonsTab,
  OfferQuickActions,
} from "../../components";
import { useOfferDetails, useOfferStatus } from "../../hooks";

export function OfferDetailsPageContent() {
  const { breadcrumbs } = useOfferDetails();
  const { getStatusInfo } = useOfferStatus();
  const offer = offerAtom.useValue();

  // Countdown timer state
  const [timeRemaining, setTimeRemaining] = useState<string | null>(null);
  const [isExpired, setIsExpired] = useState(false);

  const statusInfo = getStatusInfo();

  // Update countdown timer
  useEffect(() => {
    const endsAt = offer.endsAt;

    if (!endsAt || statusInfo.status === "expired") return;

    const calculateTimeRemaining = () => {
      const timeDiff = endsAt.timestamp - Date.now();

      if (timeDiff <= 0) {
        setIsExpired(true);
        return null;
      }

      return formatRelativeTime(endsAt);
    };

    const updateTimer = () => {
      const remaining = calculateTimeRemaining();
      setTimeRemaining(remaining);
    };

    // Initial calculation
    updateTimer();

    // Update every second
    const interval = setInterval(updateTimer, 1000);

    return () => clearInterval(interval);
  }, [offer.endsAt, statusInfo.status]);

  // Format date properly
  const formatDate = (timestamp: number) => {
    try {
      return formatLocalizedDate(timestamp, {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    } catch {
      return "Invalid Date";
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-blue-50 dark:from-gray-950 dark:via-gray-900 dark:to-blue-950/40">
      <div className="container mx-auto px-4 py-6 sm:px-6 sm:py-8 lg:px-8">
        {/* Breadcrumb */}
        <div className="mb-6 sm:mb-8">
          <Breadcrumb items={breadcrumbs} />
        </div>

        {/* Status-based Banners */}
        {statusInfo.status === "expired" && (
          <div className="mb-8 rounded-2xl bg-gradient-to-r from-gray-400 to-gray-600 p-6 shadow-xl">
            <div className="flex items-center justify-center gap-3">
              <svg
                className="h-8 w-8 text-white"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
              <span className="text-lg font-bold text-white">
                {trans("offers.offerExpired")}
              </span>
            </div>
          </div>
        )}

        {statusInfo.status === "notStarted" && (
          <div className="mb-8 rounded-2xl bg-gradient-to-r from-blue-400 to-blue-600 p-6 shadow-xl">
            <div className="flex items-center justify-center gap-3">
              <svg
                className="h-8 w-8 text-white"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z"
                  clipRule="evenodd"
                />
              </svg>
              <span className="text-lg font-bold text-white">
                {trans("offers.offerNotStarted")}
              </span>
            </div>
          </div>
        )}

        {/* Countdown Banner - if offer is expiring soon and not expired */}
        {timeRemaining &&
          !isExpired &&
          offer.endsAt &&
          statusInfo.status !== "expired" && (
            <div className="mb-8 animate-pulse rounded-2xl bg-gradient-to-r from-orange-400 via-red-500 to-pink-500 p-6 shadow-xl">
              <div className="flex flex-col items-center justify-center gap-4 sm:flex-row sm:gap-6">
                <div className="flex items-center gap-3">
                  <div className="animate-bounce">
                    <svg
                      className="h-8 w-8 text-white"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <span className="text-lg font-bold text-white">
                    {trans("offers.limitedTime")}
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="animate-pulse rounded-full bg-white px-4 py-2 text-lg font-bold text-red-600 shadow-lg">
                    {timeRemaining}
                  </div>
                  <span className="text-base font-medium text-white">
                    {trans("offers.expiresSoon")}
                  </span>
                </div>
              </div>
            </div>
          )}

        {/* Offer Header */}
        <div className="mb-8 sm:mb-10">
          <OfferHeader />
        </div>

        {/* Middle Section - Student-Friendly Design */}
        <div className="mb-8 sm:mb-12">
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            {/* Left Column - Quick Actions */}
            <div className="lg:col-span-1">
              <div className="sticky top-6">
                <OfferQuickActions />
              </div>
            </div>

            {/* Right Column - What's Included */}
            <div className="lg:col-span-1">
              <div className="transform rounded-3xl bg-gradient-to-br from-blue-500 via-purple-600 to-indigo-700 p-6 text-white shadow-2xl transition-all duration-300 hover:scale-105">
                <div className="mb-4 flex items-center gap-3">
                  <div className="rounded-full bg-white/20 p-2 backdrop-blur-sm">
                    <svg
                      className="h-6 w-6 text-white"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-bold">
                    {trans("offers.whatsIncluded")}
                  </h3>
                </div>

                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="rounded-full bg-white/20 p-1.5 backdrop-blur-sm">
                      <svg
                        className="h-4 w-4 text-white"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                    <span className="text-white">
                      {trans("grades.totalLessons", {
                        count: offer.lessons?.length || 0,
                      })}
                    </span>
                  </div>

                  {offer.salePercentage > 0 && (
                    <div className="flex items-center gap-3">
                      <div className="rounded-full bg-white/20 p-1.5 backdrop-blur-sm">
                        <svg
                          className="h-4 w-4 text-white"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>
                      <span className="text-white">
                        <span className="text-xl font-bold">
                          {offer.salePercentage}%
                        </span>{" "}
                        {trans("offers.discount")}
                      </span>
                    </div>
                  )}

                  {offer.endsAt &&
                    !isExpired &&
                    statusInfo.status !== "expired" && (
                      <div className="flex items-center gap-3">
                        <div className="rounded-full bg-white/20 p-1.5 backdrop-blur-sm">
                          <svg
                            className="h-4 w-4 text-white"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </div>
                        <div className="text-white">
                          <div className="text-sm opacity-90">
                            {trans("offers.validUntil")}
                          </div>
                          <div className="font-bold">
                            {formatDate(offer.endsAt.timestamp)}
                          </div>
                        </div>
                      </div>
                    )}

                  {offer.oldPrice > offer.price && (
                    <div className="flex items-center gap-3">
                      <div className="rounded-full bg-white/20 p-1.5 backdrop-blur-sm">
                        <svg
                          className="h-4 w-4 text-white"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>
                      <div className="text-white">
                        <div className="text-sm opacity-90">
                          {trans("offers.totalSavings")}
                        </div>
                        <div className="font-bold">
                          {trans("offers.save")} {offer.oldPrice - offer.price}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Offer Description */}
        <div className="mb-8 sm:mb-10">
          <OfferDescription />
        </div>

        <div className="mb-6">
          <Tabs defaultValue="lessons" className="w-full">
            <Tab
              value="lessons"
              label={
                trans("offers.includedLessons") +
                ` (${offer.lessons?.length || 0})`
              }
            >
              <div id="offer-content" className="mt-6">
                <OfferLessonsTab />
              </div>
            </Tab>
          </Tabs>
        </div>
      </div>
    </div>
  );
}
